import { useLayoutEffect, useState } from "react";
import MapCategory from "@components/MapCategory/index.jsx";
import ShipmentDistribution from "@components/templates/Profile/ShipmentDistribution/index.jsx";
import { getChartType, getEventName, getUserActivePlan, preparePortsMapData } from "@helpers/profileHelper.js";
import { portsCategoryData, portsEvent, profileTypeEndPoint, profileTypeEnum } from "@components/templates/Profile/ProfileHeader/profile-enum.js";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { ladingCachingHandler, portCategoryHandler, profileMapDataHandler, unLadingCachingHandler } from "@redux/PortProfile/portProfileSlice.jsx";
import { toast } from "react-toastify";
import styles from "./PortOverview.module.scss";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { cachingPanelData } from "@containers/PortProfile/utils/constant";
import NoSearchDataComponent from "@shared/country-components/no-search-data";

const PortOverview = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [mapData, setMapData] = useState([]);
  const [imageData, setImageData] = useState([]);
  const [activeCategory, setActiveCategory] = useState(portsCategoryData[0].id);
  const [homeGeoPoint, setHomeGeoPoint] = useState({});
  const [shipmentDistribution, setShipmentDistribution] = useState([]);
  const [shipmentDistributionLoader, setShipmentDistributionLoader] = useState(true);
  const { portId, portName } = useParams();
  const { search: searchUrlParams, pathname } = useLocation();
  const dataSource = new URLSearchParams(searchUrlParams).get("dataSource");
  const { ladingCaching, unladingCaching } = useSelector((state) => state.portProfile);

  const dispatch = useDispatch();
  const {
    portCategory,
    tabDetails: { activePanelId, activeTabId },
    profileRedirectionId,
    countDetails,
  } = useSelector((state) => state.portProfile);

  useLayoutEffect(() => {
    setShipmentDistributionLoader(true);
    if (cachingPanelData.lading === activePanelId && ladingCaching.shipmentDistribution) {
      let ladingCachingClone = JSON.parse(JSON.stringify(ladingCaching));
      setShipmentDistribution(ladingCachingClone.shipmentDistribution);
      setShipmentDistributionLoader(false);
    } else if (cachingPanelData.unlading === activePanelId && unladingCaching.shipmentDistribution) {
      let unladingCachingClone = JSON.parse(JSON.stringify(unladingCaching));
      setShipmentDistribution(unladingCachingClone.shipmentDistribution);
      setShipmentDistributionLoader(false);
    } else if (activePanelId && activeTabId) {
      fetchShipmentData();
    }
  }, [activeTabId, activePanelId]);

  const fetchShipmentData = async () => {
    try {
      const requestPayload = {
        profile_type: profileTypeEnum.ports,
        _id: portId,
        category: activePanelId,
        sub_category: activeTabId,
        slug: portName,
      };

      const res = await axios.post(profileTypeEndPoint.getShipmentDistribution, requestPayload);
      const { data } = res.data;
      if (data && Array.isArray(data) && data.length > 0) {
        setShipmentDistribution(data);
        if (cachingPanelData.lading === activePanelId) {
          let ladingCachingClone = JSON.parse(JSON.stringify(ladingCaching));
          ladingCachingClone.shipmentDistribution = data;
          dispatch(ladingCachingHandler(ladingCachingClone));
        } else if (cachingPanelData.unlading === activePanelId) {
          let unladingCachingClone = JSON.parse(JSON.stringify(unladingCaching));
          unladingCachingClone.shipmentDistribution = data;
          dispatch(unLadingCachingHandler(unladingCachingClone));
        }
      }
    } catch (err) {
      setError(true);
      toast.error("Something went wrong. Please try again later !!!");
    } finally {
      setShipmentDistributionLoader(false);
    }
  };

  //useLayoutEffect - need error and loading states before rendering due to tab switch
  useLayoutEffect(() => {
    if (activePanelId && activeTabId) {
      const requestPayload = {
        profile_type: profileTypeEnum.ports,
        _id: portId,
        category: activePanelId,
        sub_category: activeTabId,
        map_type: activeCategory,
      };
      setLoading(true);
      setError(false);
      if (!portCategory || portCategory !== activePanelId) {
        dispatch(
          profileMapDataHandler({
            loading: true,
          })
        );
      }
      if (cachingPanelData.lading === activePanelId && ladingCaching[activeTabId][activeCategory]) {
        const { mapData, imageData, homeGeoPoint } = ladingCaching[activeTabId][activeCategory];
        let mapDataClone = JSON.parse(JSON.stringify(mapData));
        let imageDataClone = JSON.parse(JSON.stringify(imageData));
        let homeGeoPointClone = homeGeoPoint ? JSON.parse(JSON.stringify(homeGeoPoint)) : homeGeoPoint;
        setMapData(mapDataClone);
        setImageData(imageDataClone);
        setHomeGeoPoint(homeGeoPointClone);
        setLoading(false);
        dispatch(portCategoryHandler(activePanelId));
        dispatch(profileMapDataHandler(ladingCaching?.profileDetails));
      } else if (cachingPanelData.unlading === activePanelId && unladingCaching[activeTabId][activeCategory]) {
        const { mapData, imageData, homeGeoPoint } = unladingCaching[activeTabId][activeCategory];
        let mapDataClone = JSON.parse(JSON.stringify(mapData));
        let imageDataClone = JSON.parse(JSON.stringify(imageData));
        let homeGeoPointClone = homeGeoPoint ? JSON.parse(JSON.stringify(homeGeoPoint)) : homeGeoPoint;
        setMapData(mapDataClone);
        setImageData(imageDataClone);
        setHomeGeoPoint(homeGeoPointClone);
        setLoading(false);
        dispatch(portCategoryHandler(activePanelId));
        dispatch(profileMapDataHandler(unladingCaching?.profileDetails));
      } else {
        axios
          .post(profileTypeEndPoint.ports.getMapData, requestPayload)
          .then((res) => {
            const { data, status } = res.data;
            if (status && status === "success") {
              setLoading(false);
              setError(false);
              let profileObj = {};
              const { country, longitude, latitude, country_code, name, total_shipments, trade_lane_count } = data;
              if (trade_lane_count === 0) {
                setActiveCategory(portsCategoryData[1].id);
              }
              const primaryDetails = {
                longitude: longitude ?? 0,
                latitude: latitude ?? 0,
                country: country ?? "united states",
                countryCode: country_code ?? "US",
                portName: name,
                shipments: total_shipments ?? 0,
              };
              const { mapData, imageData, homeGeoPoint } = preparePortsMapData(activeCategory, data[activeCategory], primaryDetails, activePanelId);
              setMapData(mapData);
              setImageData(imageData);
              setHomeGeoPoint(homeGeoPoint);
              const { reverse_relation, nvocc_count, vocc_count, vessel_count, importers_count, exporters_count, freight_forwarder_count, company_id, slug } = data;
              if (!portCategory || portCategory !== activePanelId) {
                profileObj = {
                  ...data,
                  nvoccCount: nvocc_count ?? 0,
                  voccCount: vocc_count ?? 0,
                  vesselCount: vessel_count ?? 0,
                  importersCount: importers_count ?? 0,
                  exportersCount: exporters_count ?? 0,
                  freightForwarderCount: freight_forwarder_count ?? 0,
                  reverseRelation: reverse_relation ?? true,
                  loading: false,
                  companyId: company_id ?? "",
                  companyName: slug ?? "",
                };
                dispatch(portCategoryHandler(activePanelId));
                dispatch(profileMapDataHandler(profileObj));
              }
              if (cachingPanelData.lading === activePanelId) {
                let ladingCachingClone = JSON.parse(JSON.stringify(ladingCaching));
                ladingCachingClone[activeTabId][activeCategory] = { mapData: mapData ? JSON.parse(JSON.stringify(mapData)) : [], imageData: imageData ? JSON.parse(JSON.stringify(imageData)) : [], homeGeoPoint: homeGeoPoint ? JSON.parse(JSON.stringify(homeGeoPoint)) : undefined };
                if (Object.keys(profileObj)?.length) {
                  ladingCachingClone.profileDetails = JSON.parse(JSON.stringify(profileObj));
                }
                dispatch(ladingCachingHandler(ladingCachingClone));
              } else if (cachingPanelData.unlading === activePanelId) {
                let unladingCachingClone = JSON.parse(JSON.stringify(unladingCaching));
                unladingCachingClone[activeTabId][activeCategory] = { mapData: mapData ? JSON.parse(JSON.stringify(mapData)) : [], imageData: imageData ? JSON.parse(JSON.stringify(imageData)) : [], homeGeoPoint: homeGeoPoint ? JSON.parse(JSON.stringify(homeGeoPoint)) : undefined };
                if (Object.keys(profileObj)?.length) {
                  unladingCachingClone.profileDetails = JSON.parse(JSON.stringify(profileObj));
                }
                dispatch(unLadingCachingHandler(unladingCachingClone));
              }
            } else {
              throw new Error("");
            }
          })
          .catch(() => {
            setLoading(false);
            setError(true);
            toast.error("Something went wrong. Please try again later !!!");
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, activePanelId, activeTabId]);

  const handleCategoryClick = (category) => {
    trademoTrackingEvents(getEventName(profileTypeEnum.ports, portsEvent.overview[category?.eventEnum]), generateTrackingObject(dataSource, pathname, getUserActivePlan()));
    setActiveCategory(category.id);
  };

  const viewShipmentsEvent = () => {
    trademoTrackingEvents(getEventName(profileTypeEnum.ports, portsEvent.viewShipments), generateTrackingObject(dataSource, pathname, getUserActivePlan()));
  };

  return error ? (
    <div className={styles.noDataContainer}>
      <NoSearchDataComponent content={"Unable to fetch the data at current moment."} />
    </div>
  ) : (
    <div className={styles.tradeOverviewContainer}>
      <MapCategory countDetails={countDetails} categoryData={portsCategoryData} activeCategory={activeCategory} handleCategoryClick={handleCategoryClick} showTradeLane={true} />
      <div className={styles.renderMapContainer}>{getChartType(activeCategory, mapData, loading, error, imageData, homeGeoPoint)}</div>
      <ShipmentDistribution shipmentDistribution={shipmentDistribution} shipmentDistributionLoader={shipmentDistributionLoader} profileType={profileTypeEnum.ports} profileId={portId} category={activePanelId} subCategory={activeTabId} activeCategory={activeCategory} profileName={portName} viewShipmentsEvent={viewShipmentsEvent} profileRedirectionId={profileRedirectionId} />
    </div>
  );
};

export default PortOverview;
